import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchVisits } from "../../store/slices/visitSlice";

const Visits = () => {
  const dispatch = useDispatch();
  const { visits, loading, error } = useSelector((s) => s.visits);

  const [filter, setFilter] = useState("all");

  // Get today's date as YYYY-MM-DD
  const today = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  useEffect(() => {
    const params = { date: today };
    dispatch(fetchVisits(params));
  }, [dispatch, today]);

  const normalizeStatus = (status) => {
    if (!status) return "";
    const s = String(status).trim().toLowerCase();
    if (s === "inprogress") return "in_progress";
    if (s === "pending") return "registered";
    return s;
  };

  const visitsList = useMemo(
    () => (Array.isArray(visits) ? visits : visits?.data || []),
    [visits]
  );

  // Local status filter
  const filteredVisits = useMemo(() => {
    if (filter === "all") return visitsList;
    return visitsList.filter((v) => normalizeStatus(v.status) === filter);
  }, [visitsList, filter]);

  const getStatusBadge = (status) => {
    const key = normalizeStatus(status);
    const statusConfig = {
      registered: { color: "bg-blue-100 text-blue-800", label: "Registered" },
      in_progress: {
        color: "bg-orange-100 text-orange-800",
        label: "In Progress",
      },
      sample_collected: {
        color: "bg-purple-100 text-purple-800",
        label: "Sample Collected",
      },
      completed: { color: "bg-green-100 text-green-800", label: "Completed" },
      cancelled: { color: "bg-gray-100 text-gray-800", label: "Cancelled" },
    };
    const cfg = statusConfig[key] || statusConfig.registered;
    return `px-2 py-1 text-xs rounded-full ${cfg.color}`;
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      routine: { color: "bg-gray-100 text-gray-800", label: "Routine" },
      urgent: { color: "bg-orange-100 text-orange-800", label: "Urgent" },
      emergency: { color: "bg-red-100 text-red-800", label: "Emergency" },
    };
    const cfg = priorityConfig[priority] || priorityConfig.routine;
    return `px-2 py-1 text-xs rounded-full ${cfg.color}`;
  };

  const formatTime = (t) => {
    if (!t) return "-";
    // Expecting HH:MM:SS or HH:MM; show HH:MM
    return t.slice(0, 5);
  };

  return (
    <div className="pt-6">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
          <div className="border-t-4 border-[#36F1A2] bg-gradient-to-r from-gray-50 to-white px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Reception
                </div>
                <h1 className="mt-1 text-xl font-semibold text-gray-900">Today’s visits</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Manage visits for {new Date().toLocaleDateString()}.
                </p>
              </div>
              <div className="text-xs text-gray-600">Date: {today}</div>
            </div>
          </div>

          <div className="space-y-6 px-5 py-5 sm:px-6">
            {error && (
              <div className="rounded-2xl bg-red-50 p-4 text-sm text-red-700 ring-1 ring-red-200">
                {String(error)}
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Total</div>
                <div className="mt-2 text-2xl font-bold text-[#235F72]">{visitsList.length}</div>
              </div>
              <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Completed</div>
                <div className="mt-2 text-2xl font-bold text-[#235F72]">
                  {visitsList.filter((v) => normalizeStatus(v.status) === "completed").length}
                </div>
              </div>
              <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">In progress</div>
                <div className="mt-2 text-2xl font-bold text-[#235F72]">
                  {visitsList.filter((v) => normalizeStatus(v.status) === "in_progress").length}
                </div>
              </div>
              <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Registered</div>
                <div className="mt-2 text-2xl font-bold text-[#235F72]">
                  {visitsList.filter((v) => normalizeStatus(v.status) === "registered").length}
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-white ring-1 ring-gray-200/70">
              <div className="border-b border-gray-100 px-4 py-3">
                <div className="text-sm font-semibold text-gray-900">Filter</div>
                <div className="mt-0.5 text-xs text-gray-600">Filter visits by status.</div>
              </div>
              <div className="flex flex-wrap gap-2 p-4">
                {["all", "registered", "in_progress", "completed"].map((key) => {
                  const active = filter === key;
                  const label =
                    key === "all"
                      ? "All"
                      : key === "in_progress"
                      ? "In progress"
                      : key.charAt(0).toUpperCase() + key.slice(1);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFilter(key)}
                      className={
                        "rounded-full px-4 py-2 text-sm font-semibold ring-1 transition focus:outline-none focus:ring-2 focus:ring-[#36F1A2] " +
                        (active
                          ? "bg-[#235F72] text-white ring-[#235F72]/20"
                          : "bg-gray-50 text-gray-700 ring-gray-200 hover:bg-gray-100")
                      }
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200/70">
              {loading ? (
                <div className="px-4 py-12 text-center text-sm text-gray-600">
                  Loading visits…
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                          <th className="px-4 py-3">Patient</th>
                          <th className="px-4 py-3">Time</th>
                          <th className="px-4 py-3">Priority</th>
                          <th className="px-4 py-3">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {filteredVisits.map((visit) => {
                          const patientName =
                            visit.patient_name ||
                            visit.full_name ||
                            `Patient #${visit.patient_id}`;
                          const cardNumber = visit.card_number
                            ? `(${visit.card_number})`
                            : "";
                          return (
                            <tr key={visit.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">
                                  {patientName}{" "}
                                  <span className="font-normal text-gray-500">{cardNumber}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-gray-700">
                                {formatTime(visit.visit_time)}
                              </td>
                              <td className="px-4 py-3">
                                <span className={getPriorityBadge(visit.priority)}>
                                  {visit.priority}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={getStatusBadge(visit.status)}>
                                  {(visit.status || "").replace("_", " ")}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {filteredVisits.length === 0 && (
                    <div className="px-4 py-12 text-center text-sm text-gray-600">
                      No visits match your filter.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Visits;
